/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './discharge-summary.reducer';

export const DischargeSummary = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const dischargeSummaryList = useAppSelector(state => state.dischargeSummary.entities);
  const loading = useAppSelector(state => state.dischargeSummary.loading);
  const totalItems = useAppSelector(state => state.dischargeSummary.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="discharge-summary-heading" data-cy="DischargeSummaryHeading">
        <Translate contentKey="tinyMedicRestApp.dischargeSummary.home.title">Discharge Summaries</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.dischargeSummary.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/discharge-summary/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.dischargeSummary.home.createLabel">Create new Discharge Summary</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.dischargeSummary.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {dischargeSummaryList && dischargeSummaryList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('summaryoftreatment')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.summaryoftreatment">Summaryoftreatment</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('summaryoftreatment')} />
                </th>
                <th className="hand" onClick={sort('admissiondiagnosis')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.admissiondiagnosis">Admissiondiagnosis</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('admissiondiagnosis')} />
                </th>
                <th className="hand" onClick={sort('finaldiagnosis')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.finaldiagnosis">Finaldiagnosis</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('finaldiagnosis')} />
                </th>
                <th className="hand" onClick={sort('previousmedicines')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.previousmedicines">Previousmedicines</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('previousmedicines')} />
                </th>
                <th className="hand" onClick={sort('treatementmedicines')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.treatementmedicines">Treatementmedicines</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('treatementmedicines')} />
                </th>
                <th className="hand" onClick={sort('emergencyPlan')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.emergencyPlan">Emergency Plan</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('emergencyPlan')} />
                </th>
                <th className="hand" onClick={sort('nextVisit')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.nextVisit">Next Visit</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('nextVisit')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('timestampentry')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.timestampentry">Timestampentry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampentry')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('lastupdated')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.lastupdated">Lastupdated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastupdated')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('dischargeType')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.dischargeType">Discharge Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('dischargeType')} />
                </th>
                <th className="hand" onClick={sort('dischargeStatus')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.dischargeStatus">Discharge Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('dischargeStatus')} />
                </th>
                <th className="hand" onClick={sort('sourceOfHistory')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.sourceOfHistory">Source Of History</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('sourceOfHistory')} />
                </th>
                <th className="hand" onClick={sort('chiefComplaints')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.chiefComplaints">Chief Complaints</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('chiefComplaints')} />
                </th>
                <th className="hand" onClick={sort('historyOfPresentIllness')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.historyOfPresentIllness">History Of Present Illness</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('historyOfPresentIllness')} />
                </th>
                <th className="hand" onClick={sort('medicalHistory')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.medicalHistory">Medical History</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('medicalHistory')} />
                </th>
                <th className="hand" onClick={sort('familyHistory')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.familyHistory">Family History</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('familyHistory')} />
                </th>
                <th className="hand" onClick={sort('historyOfAllergies')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.historyOfAllergies">History Of Allergies</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('historyOfAllergies')} />
                </th>
                <th className="hand" onClick={sort('historyOfImmunization')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.historyOfImmunization">History Of Immunization</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('historyOfImmunization')} />
                </th>
                <th className="hand" onClick={sort('systemicExamination')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.systemicExamination">Systemic Examination</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('systemicExamination')} />
                </th>
                <th className="hand" onClick={sort('discussion')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.discussion">Discussion</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('discussion')} />
                </th>
                <th className="hand" onClick={sort('dietInstructions')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.dietInstructions">Diet Instructions</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('dietInstructions')} />
                </th>
                <th className="hand" onClick={sort('followupInstructions')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.followupInstructions">Followup Instructions</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('followupInstructions')} />
                </th>
                <th className="hand" onClick={sort('physicalActivityInstructions')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.physicalActivityInstructions">
                    Physical Activity Instructions
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('physicalActivityInstructions')} />
                </th>
                <th className="hand" onClick={sort('normalcyAfter')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.normalcyAfter">Normalcy After</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('normalcyAfter')} />
                </th>
                <th className="hand" onClick={sort('surgicalWoundClear')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.surgicalWoundClear">Surgical Wound Clear</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('surgicalWoundClear')} />
                </th>
                <th className="hand" onClick={sort('painScore')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.painScore">Pain Score</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('painScore')} />
                </th>
                <th className="hand" onClick={sort('vitalsStable')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.vitalsStable">Vitals Stable</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('vitalsStable')} />
                </th>
                <th className="hand" onClick={sort('ambulatory')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.ambulatory">Ambulatory</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('ambulatory')} />
                </th>
                <th className="hand" onClick={sort('intiitiatedById')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.intiitiatedById">Intiitiated By Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('intiitiatedById')} />
                </th>
                <th className="hand" onClick={sort('modifiedById')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.modifiedById">Modified By Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('modifiedById')} />
                </th>
                <th className="hand" onClick={sort('intiitiatedByType')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.intiitiatedByType">Intiitiated By Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('intiitiatedByType')} />
                </th>
                <th className="hand" onClick={sort('modifiedByType')}>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.modifiedByType">Modified By Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('modifiedByType')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.admission">Admission</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.patient">Patient</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.prescription">Prescription</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.dischargeSummary.approvedBy">Approved By</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {dischargeSummaryList.map((dischargeSummary, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/discharge-summary/${dischargeSummary.id}`} color="link" size="sm">
                      {dischargeSummary.id}
                    </Button>
                  </td>
                  <td>{dischargeSummary.summaryoftreatment}</td>
                  <td>{dischargeSummary.admissiondiagnosis}</td>
                  <td>{dischargeSummary.finaldiagnosis}</td>
                  <td>{dischargeSummary.previousmedicines}</td>
                  <td>{dischargeSummary.treatementmedicines}</td>
                  <td>{dischargeSummary.emergencyPlan}</td>
                  <td>{dischargeSummary.nextVisit}</td>
                  <td>{dischargeSummary.active ? 'true' : 'false'}</td>
                  <td>{dischargeSummary.timestampentry}</td>
                  <td>{dischargeSummary.microSec}</td>
                  <td>{dischargeSummary.lastupdated}</td>
                  <td>{dischargeSummary.cursor}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.DischargeType.${dischargeSummary.dischargeType}`} />
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.DischargeStatus.${dischargeSummary.dischargeStatus}`} />
                  </td>
                  <td>{dischargeSummary.sourceOfHistory}</td>
                  <td>{dischargeSummary.chiefComplaints}</td>
                  <td>{dischargeSummary.historyOfPresentIllness}</td>
                  <td>{dischargeSummary.medicalHistory}</td>
                  <td>{dischargeSummary.familyHistory}</td>
                  <td>{dischargeSummary.historyOfAllergies}</td>
                  <td>{dischargeSummary.historyOfImmunization}</td>
                  <td>{dischargeSummary.systemicExamination}</td>
                  <td>{dischargeSummary.discussion}</td>
                  <td>{dischargeSummary.dietInstructions}</td>
                  <td>{dischargeSummary.followupInstructions}</td>
                  <td>{dischargeSummary.physicalActivityInstructions}</td>
                  <td>{dischargeSummary.normalcyAfter}</td>
                  <td>{dischargeSummary.surgicalWoundClear}</td>
                  <td>{dischargeSummary.painScore}</td>
                  <td>{dischargeSummary.vitalsStable ? 'true' : 'false'}</td>
                  <td>{dischargeSummary.ambulatory ? 'true' : 'false'}</td>
                  <td>{dischargeSummary.intiitiatedById}</td>
                  <td>{dischargeSummary.modifiedById}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.AddedByType.${dischargeSummary.intiitiatedByType}`} />
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.AddedByType.${dischargeSummary.modifiedByType}`} />
                  </td>
                  <td>
                    {dischargeSummary.admission ? (
                      <Link to={`/admission/${dischargeSummary.admission.id}`}>{dischargeSummary.admission.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {dischargeSummary.patient ? (
                      <Link to={`/gluco-user/${dischargeSummary.patient.id}`}>{dischargeSummary.patient.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {dischargeSummary.prescription ? (
                      <Link to={`/prescription/${dischargeSummary.prescription.id}`}>{dischargeSummary.prescription.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {dischargeSummary.approvedBy ? (
                      <Link to={`/doctor/${dischargeSummary.approvedBy.id}`}>{dischargeSummary.approvedBy.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/discharge-summary/${dischargeSummary.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/discharge-summary/${dischargeSummary.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/discharge-summary/${dischargeSummary.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.dischargeSummary.home.notFound">No Discharge Summaries found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={dischargeSummaryList && dischargeSummaryList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default DischargeSummary;
